import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AgentEvents, DashboardReadStatusChangedEvent } from '@dreamai/types';
import api from '../../api';
import { ReactComponent as NotificationIndicator } from '../../icons/notification-indicator.svg';
import { usePusher } from '../../contexts/PusherContext';
import './has-unread-notification-count.scss';

interface HasUnreadNotificationCountProps {
  agentId?: string;
  children?: React.ReactNode;
}

const HasUnreadNotificationCount: React.FC<HasUnreadNotificationCountProps> = ({
  agentId,
  children,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const pusherContext = usePusher(true); // Get full context to access both channels
  const userChannel = pusherContext?.userChannel;
  const companyChannel = pusherContext?.pusherChannel;
  const [hasUnread, setHasUnread] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchUnreadStatus = async () => {
    try {
      const token = await getAccessTokenSilently();

      const url = agentId
        ? `/agent-dashboard/has-unread?agentId=${agentId}`
        : '/agent-dashboard/has-unread';

      const response = await api.get<{
        success: boolean;
        has_unread: boolean;
      }>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setHasUnread(response.data.has_unread);
      }
    } catch (err) {
      console.error('Error fetching unread status:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchUnreadStatus();
  }, [agentId, getAccessTokenSilently]);

  // Listen for Pusher events to refresh unread status
  // Listen on BOTH user channel (for mark-as-read) and company channel (for new summaries)
  useEffect(() => {
    if (!userChannel || !companyChannel) return;

    const handleReadStatusChanged = (data: DashboardReadStatusChangedEvent) => {
      // If this component is for a specific agent, only update if the event is for that agent
      if (agentId && data.agentId !== agentId) {
        return;
      }

      // If this component is for global (no agentId), only update if the event is also global
      // This prevents a specific agent's mark-as-read from triggering a global refetch
      if (!agentId && data.agentId) {
        return;
      }

      // If we get here, either:
      // 1. This is for a specific agent and the event matches that agent, OR
      // 2. This is global and the event is also global
      fetchUnreadStatus();
    };

    // Listen on user channel for mark-as-read events (hasUnread: false)
    userChannel.bind(
      AgentEvents.DASHBOARD_READ_STATUS_CHANGED,
      handleReadStatusChanged,
    );

    // Listen on company channel for new summary events (hasUnread: true)
    companyChannel.bind(
      AgentEvents.DASHBOARD_READ_STATUS_CHANGED,
      handleReadStatusChanged,
    );

    return () => {
      userChannel.unbind(
        AgentEvents.DASHBOARD_READ_STATUS_CHANGED,
        handleReadStatusChanged,
      );
      companyChannel.unbind(
        AgentEvents.DASHBOARD_READ_STATUS_CHANGED,
        handleReadStatusChanged,
      );
    };
  }, [userChannel, companyChannel, agentId]);

  // Don't render anything while loading
  if (loading) {
    return children ? <>{children}</> : null;
  }

  // If no children, show the notification indicator icon when there are unread items
  if (!children) {
    return hasUnread ? (
      <div className='has-unread-notification-wrapper'>
        <NotificationIndicator />
      </div>
    ) : null;
  }

  // If children provided, wrap them and add has-unread class if there are notifications
  return (
    <div
      className={`has-unread-notification-wrapper ${hasUnread ? 'has-unread' : ''}`}
    >
      {children}
    </div>
  );
};

export default HasUnreadNotificationCount;
